import { PublicCapTablePayload } from './shared-cap-table.types';

const backendUrl = process.env.REACT_APP_BACKEND_URL ?? '';

export enum PublicCapTableError {
  /** The owner switched export off while the viewer had the page open. */
  EXPORT_DISABLED = 'export-disabled',
  GONE = 'gone',
  PASSWORD_REQUIRED = 'password-required',
  THROTTLED = 'throttled',
  UNKNOWN = 'unknown',
}

export class PublicCapTableRequestError extends Error {
  constructor(
    readonly kind: PublicCapTableError,
    /** Seconds left on the throttle window; only on THROTTLED. */
    readonly retryAfterSeconds?: number,
    /** Shown on the gate before the password is accepted (CT-9 §8); only on PASSWORD_REQUIRED. */
    readonly companyName?: null | string,
    readonly companyLogoUrl?: null | string,
  ) {
    super(kind);
    this.name = 'PublicCapTableRequestError';
  }
}

const toError = async (response: Response): Promise<PublicCapTableRequestError> => {
  const body = await response.json().catch(() => ({}));

  if (response.status === 401) {
    const gate = body as {
      companyLogoUrl?: null | string;
      companyName?: null | string;
      // Present when the unlock window is already spent, so the gate opens locked rather than
      // inviting a guess the throttle will refuse.
      retryAfterSeconds?: number;
    };

    return new PublicCapTableRequestError(
      PublicCapTableError.PASSWORD_REQUIRED,
      gate.retryAfterSeconds,
      gate.companyName ?? null,
      gate.companyLogoUrl ?? null,
    );
  }

  if (response.status === 403) {
    return new PublicCapTableRequestError(PublicCapTableError.EXPORT_DISABLED);
  }

  if (response.status === 404) {
    return new PublicCapTableRequestError(PublicCapTableError.GONE);
  }

  if (response.status === 429) {
    return new PublicCapTableRequestError(
      PublicCapTableError.THROTTLED,
      (body as { retryAfterSeconds?: number }).retryAfterSeconds,
    );
  }

  return new PublicCapTableRequestError(PublicCapTableError.UNKNOWN);
};

/**
 * Bypasses `ApiService`, which attaches a bearer token and runs a refresh-then-logout path on 401.
 * Here 401 is the ordinary "password required" answer, so that logic would sign out an owner
 * opening their own link. No bearer token is ever attached; the only credential that travels is the
 * httpOnly unlock cookie the server sets once a password has been accepted, which is what keeps a
 * reload from asking again.
 */
const request = async (path: string, init?: RequestInit): Promise<PublicCapTablePayload> => {
  // The header only on requests that carry a body: on a bodyless GET it is what turns an ordinary
  // request into a preflighted one, so every view of the public page paid for an extra round trip.
  const response = await fetch(`${backendUrl}/shared-cap-table${path}`, {
    ...init,
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json', ...init.headers } : init?.headers,
  });

  if (!response.ok) {
    throw await toError(response);
  }

  return (await response.json()) as PublicCapTablePayload;
};

export const fetchPublicCapTable = (linkId: string) => request(`/${linkId}`);

/**
 * Returns the spreadsheet as a blob rather than navigating: the route is a POST (the password
 * travels in the body), so a plain link or window.open cannot reach it.
 */
export const downloadPublicCapTable = async (
  linkId: string,
  password: string | undefined,
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await fetch(`${backendUrl}/shared-cap-table/${linkId}/export`, {
    body: JSON.stringify(password ? { password } : {}),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

  if (!response.ok) {
    throw await toError(response);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const [, encodedName] = disposition.match(/filename=([^;]+)/) ?? [];

  return {
    blob: await response.blob(),
    fileName: encodedName ? decodeURIComponent(encodedName) : 'CapTable.xlsx',
  };
};

export const unlockPublicCapTable = (linkId: string, password: string) =>
  request(`/${linkId}/unlock`, {
    body: JSON.stringify({ password }),
    method: 'POST',
  });
